import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type FannieMaeUnderwriteFormValues = {
  accountIdentifier: string;
  accountPassword: string;
  softwareProvider: string;
};

interface EditFannieMaeUnderwriteIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (_settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditFannieMaeUnderwriteIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditFannieMaeUnderwriteIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let accountIdentifier = '';
  let accountPassword = '';
  let softwareProvider = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    accountIdentifier = data.accountIdentifier || '';
    accountPassword = data.accountPassword || '';
    softwareProvider = data.softwareProvider || '';
  }

  const validationSchema = yup.object().shape({
    accountIdentifier: yup.string().required('Account Identifier is required'),
    accountPassword: yup.string().required('Account Password is required'),
    softwareProvider: yup.string().required('Software Provider is required'),
  });

  const { control, handleSubmit, formState } = useForm<FannieMaeUnderwriteFormValues>({
    defaultValues: {
      accountIdentifier: accountIdentifier,
      accountPassword: accountPassword,
      softwareProvider: softwareProvider,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Fannie Mae Underwrite Settings' : 'Fannie Mae Underwrite Settings'}</h3>
      <form
        autoComplete="off"
        onSubmit={handleSubmit((data) => {
          console.log('Form validated successfully:', data);

          if (onSave) {
            let settings: IntegrationSettings;

            if (isEditMode && integrationSettings) {
              settings = {
                ...integrationSettings,
                environment: environment,
                integrationData: JSON.stringify({
                  accountIdentifier: data.accountIdentifier,
                  accountPassword: data.accountPassword,
                  softwareProvider: data.softwareProvider,
                }),
              };
            } else {
              settings = {
                name: 'Fannie Mae Underwrite Integration',
                vendor: IntegrationVendor.FannieMae,
                status: IntegrationStatus.Active,
                environment: environment,
                integrationData: JSON.stringify({
                  accountIdentifier: data.accountIdentifier,
                  accountPassword: data.accountPassword,
                  softwareProvider: data.softwareProvider,
                }),
              };
            }

            onSave(settings);
          }
        })}
        noValidate
        className="pb-3 p-fluid p-input-filled"
      >
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="accountIdentifier"
            label="Account Identifier *"
            placeholder="Enter Account Identifier"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="accountPassword"
            label="Account Password *"
            placeholder="Enter Account Password"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="softwareProvider"
            label="Software Provider *"
            placeholder="Enter Software Provider"
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-4" style={{ zIndex: 1000 }}>
          <Button
            type="button"
            label="Cancel"
            className="p-button p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
